import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../../utils/constants';
import { useAuth } from '../../../utils/context';
import { useSearch } from '../../../utils/SearchProvider';

const UserPerformance = () => {
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedTestType, setSelectedTestType] = useState('mock');
    const [rankings, setRankings] = useState([]);
    const [filteredRankings, setFilteredRankings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [institute, setInstitute] = useState('');
    const [error, setError] = useState(null);
    const [availableSubjects, setAvailableSubjects] = useState([]);

    const { searchTerm } = useSearch();
    const [auth] = useAuth();

    const testTypes = ['mock', 'practice'];

    const formatSubjectForAPI = (subject) => {
        return subject.toLowerCase().replace(/\s+/g, '_');
    };

    useEffect(() => {
        try {
            const userData = auth?.user?.selectedSubjects || [];
            const subjects = userData.map(subject => subject.subjectName);

            setAvailableSubjects(subjects);

            if (!selectedSubject && subjects.length > 0) {
                setSelectedSubject(subjects[0]);
            }
        } catch (error) {
            console.error("Error loading subjects from local storage:", error);
            setError("Failed to load subjects. Please refresh the page.");
        }
    }, [auth?.user, selectedSubject]);

    const fetchLeaderboardData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const formattedSubject = formatSubjectForAPI(selectedSubject);

            const response = await axios.get(
                `${API}/scores/ranking/topic/${auth.user?._id}?topicName=${formattedSubject}&testType=${selectedTestType}`,
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken.token}`,
                    },
                }
            );

            if (response.data.success) {
                setRankings(response.data.rankings);
                setFilteredRankings(response.data.rankings);
                setInstitute(response.data.institute);
            } else {
                setError(response.data.message === "User does not have an associated institute."
                    ? "You don't have an associated institute. Please contact Us."
                    : "Failed to load rankings. Please try again.");
            }
        } catch (error) {
            const message = error.response?.data?.message;
            setError(message === "User does not have an associated institute."
                ? "You don't have an associated institute. Please contact Us."
                : "Network error. Please check your connection and try again.");
        } finally {
            setIsLoading(false);
        }
    }, [selectedSubject, selectedTestType, auth?.accessToken?.token, auth.user?._id]);

    useEffect(() => {
        if (selectedSubject && selectedTestType && auth?.accessToken?.token) {
            fetchLeaderboardData();
        }
    }, [fetchLeaderboardData, selectedTestType, selectedSubject, auth?.accessToken?.token]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredRankings(rankings);
        } else {
            const filtered = rankings.filter(student =>
                student.userName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredRankings(filtered);
        }
    }, [searchTerm, rankings]);

    const handleSubjectChange = (e) => {
        setSelectedSubject(e.target.value);
    };

    const handleTestTypeChange = (e) => {
        setSelectedTestType(e.target.value);
    };

    const formatTime = (timeInSeconds) => {
        if (timeInSeconds === undefined || timeInSeconds === null) return 'N/A';
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes} min ${seconds} sec`;
    };

    return (
        <div className="w-full min-h-full rounded-3xl mx-auto p-4 bg-white">
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-xl font-bold">Leaderboard</h1>
                        {institute && <p className="text-gray-600">Institute: {institute}</p>}
                    </div>
                    {searchTerm && (
                        <div className="text-blue-600">
                            {filteredRankings.length} result{filteredRankings.length !== 1 ? 's' : ''} for "{searchTerm}"
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 mb-4">
                    <div className="w-48">
                        <select
                            value={selectedSubject}
                            onChange={handleSubjectChange}
                            className="w-full p-2 border border-gray-300 rounded-md"
                            disabled={availableSubjects.length === 0}
                        >
                            {availableSubjects.length === 0 ? (
                                <option value="">No subjects available</option>
                            ) : (
                                availableSubjects.map((subject) => (
                                    <option key={subject} value={subject}>{subject}</option>
                                ))
                            )}
                        </select>
                    </div>
                    <div className="w-48">
                        <select
                            value={selectedTestType}
                            onChange={handleTestTypeChange}
                            className="w-full p-2 border border-gray-300 rounded-md"
                        >
                            {testTypes.map((type) => (
                                <option key={type} value={type}>{type === 'mock' ? 'Mock Test' : 'Practice Test'}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            {error ? (
                <div className="text-center py-10">
                    <p className="text-red-500">{error}</p>
                </div>
            ) : isLoading ? (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-solid border-blue-500"></div>
                </div>
            ) : filteredRankings.length === 0 ? (
                <div className="text-center py-10">
                    <p className="text-gray-500">
                        {searchTerm ? `No students found matching "${searchTerm}"` : "Your ranking's on hold… Start practicing to unlock it!"}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-blue-50">
                                <th className="p-3 text-left border border-gray-200">Rank</th>
                                <th className="p-3 text-left border border-gray-200">Student Name</th>
                                <th className="p-3 text-left border border-gray-200">Best Score</th>
                                <th className="p-3 text-left border border-gray-200">Last Score</th>
                                <th className="p-3 text-left border border-gray-200">Best Time</th>
                                <th className="p-3 text-left border border-gray-200">Last Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRankings.map((student) => (
                                <tr key={student.userId} className="hover:bg-gray-50">
                                    <td className="p-3 border-b border-gray-200">#{student.rank}</td>
                                    <td className="p-3 border-b border-gray-200 flex items-center gap-2">
                                        {student.profilePic && (
                                            <img src={student.profilePic} alt={`${student.userName}'s avatar`} className="w-8 h-8 rounded-full" />
                                        )}
                                        {student.userName}
                                    </td>
                                    <td className="p-3 border-b border-gray-200">{student.bestScore}%</td>
                                    <td className="p-3 border-b border-gray-200">{student.lastScore}%</td>
                                    <td className="p-3 border-b border-gray-200">{formatTime(student.bestTime)}</td>
                                    <td className="p-3 border-b border-gray-200">{formatTime(student.lastTime)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UserPerformance;